/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { useEffect, useState } from 'react';
import { useRootSelector } from '../../../../hooks/useRootSelector';
import { SigningActionType } from '../../../../shared/constants/signing';
import { DocumentStatus } from '../../../../shared/constants/document';
import { ReviewDocument } from 'src/types/documents';
import { getDocumentPdfUrlForReview } from '@utils/documents';
import { isEqual } from '@utils/lodash';

export const useReviewDocumentPdfs = () => {
  const [reviewDocuments, setReviewDocuments] = useState<ReviewDocument[]>([]);
  const { callDetails, document, signingRoom } = useRootSelector(state => state);
  const { documents } = document;

  const fetchReviewDocuments = async () => {
    if (!callDetails || !documents) {
      return;
    }

    const filteredReviewDocs = documents.filter(d => d.status === DocumentStatus.PENDING_REVIEW);
    const hasNewReviewDocs = !isEqual(
      filteredReviewDocs.map(d => d.id),
      reviewDocuments.map(d => d.id),
    );
    const shouldUpdateReviewDocs = signingRoom.signingAction.type === SigningActionType.REVIEW && hasNewReviewDocs;
    if (shouldUpdateReviewDocs) {
      const pdfUrls = await Promise.all(
        filteredReviewDocs.map(async doc => {
          const pdfUrl = await getDocumentPdfUrlForReview(doc.id, callDetails.id);
          return {
            id: doc.id,
            fileName: doc.name,
            pdfUrl,
          };
        }),
      );
      setReviewDocuments(pdfUrls);
    }
  };

  useEffect(() => {
    fetchReviewDocuments();
  }, [document.documents, callDetails?.id, signingRoom.signingAction.type]);

  return reviewDocuments;
};
